// Aba "Memória" — lista/CRUD das memórias do Dwight (escopo usuário/cliente/time).
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Button, Tag, Table, Typography, Space, Flex, App as AntApp, Empty, Radio,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDateTime } from './utils';
import ModalMemoria from './ModalMemoria';
import type { MemoriaItem } from './types';

const { Text } = Typography;

export default function MemoriaTab() {
  const { modal } = AntApp.useApp();
  const [items, setItems] = useState<MemoriaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroEscopo, setFiltroEscopo] = useState<'todos' | 'usuario' | 'cliente' | 'time'>('todos');
  const [editando, setEditando] = useState<MemoriaItem | null>(null);
  const [criando, setCriando] = useState(false);

  useEffect(() => { carregar(); }, []);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/agentes/memoria`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }

  function excluir(id: string) {
    modal.confirm({
      title: 'Apagar esta memória?', content: 'O Dwight perderá esse contexto.',
      okText: 'Apagar', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        const token = await getToken();
        await axios.delete(`${API_URL}/agentes/memoria/${id}`, { headers: { Authorization: token } });
        await carregar();
      },
    });
  }

  const filtrados = filtroEscopo === 'todos' ? items : items.filter(i => i.escopo === filtroEscopo);

  const cols: ColumnsType<MemoriaItem> = [
    {
      title: 'Escopo', dataIndex: 'escopo', key: 'es', width: 100,
      render: (v) => <Tag color={v === 'usuario' ? 'blue' : v === 'time' ? 'gold' : 'purple'}>{v}</Tag>,
    },
    { title: 'Chave', dataIndex: 'chave', key: 'ch', render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
    { title: 'Valor', dataIndex: 'valor', key: 'v' },
    { title: 'Atualizado', dataIndex: 'atualizado_em', key: 'at', width: 140, render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtDateTime(v)}</Text> },
    {
      title: '', key: 'ac', width: 90, align: 'right',
      render: (_, m) => (
        <Space size={2}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(m)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => excluir(m.id)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Flex align="center" gap={10} style={{ marginBottom: 12 }}>
        <Radio.Group value={filtroEscopo} onChange={(e) => setFiltroEscopo(e.target.value)} size="small">
          {[
            { id: 'todos', label: `Todas (${items.length})` },
            { id: 'usuario', label: `Usuário (${items.filter(i => i.escopo === 'usuario').length})` },
            { id: 'cliente', label: `Cliente (${items.filter(i => i.escopo === 'cliente').length})` },
            { id: 'time', label: `Time (${items.filter(i => i.escopo === 'time').length})` },
          ].map(o => <Radio.Button key={o.id} value={o.id}>{o.label}</Radio.Button>)}
        </Radio.Group>
        <Button type="primary" icon={<PlusOutlined />} style={{ marginLeft: 'auto' }} onClick={() => setCriando(true)}>
          Adicionar memória
        </Button>
      </Flex>
      <Table rowKey="id" columns={cols} dataSource={filtrados} loading={loading}
        size="small" pagination={{ pageSize: 50 }}
        locale={{ emptyText: <Empty description="Nenhuma memória. O Dwight aprende ao longo das conversas." /> }} />

      {(editando || criando) && (
        <ModalMemoria item={editando}
          onClose={() => { setEditando(null); setCriando(false); }}
          onSalvo={() => { setEditando(null); setCriando(false); carregar(); }} />
      )}
    </div>
  );
}
